import * as React from 'react';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router';
import { IExtractionBatch } from '../../../types/my-types';

import useExtractionBatchSearchParams from '../../hooks/useExtractionBatchSearchParams';

import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import MenuItemHiddenTextLabel from '../../common/MenuItemHiddenTextLabel';

export interface ExtractionBatchDeleteProps {
  extractionBatchId: string;
  type: 'menu' | 'icon';
  handleClose?: () => void;
}

const ExtractionBatchDelete: React.FC<ExtractionBatchDeleteProps> = ({
  extractionBatchId,
  type,
  handleClose,
}) => {
  const { setToast, dataPoolId } = React.useContext(AppContext);
  const [open, setOpen] = React.useState(false);

  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
    runHandleClose();
  };

  const runHandleClose = () => {
    if (handleClose) {
      handleClose();
    }
  };

  const { generateSearchParams } = useExtractionBatchSearchParams();

  const glynt = useGlynt();
  const client = useQueryClient();
  const searchParams = generateSearchParams(dataPoolId);
  const deleteExtractionBatch = useMutation({
    mutationFn: async () => await (await glynt).extractionBatch.del(extractionBatchId),

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['extractionBatches']
      });

      if (type === 'icon') {
        navigate(`/data-pools/${dataPoolId}/extraction-batches`);
      }

      setToast({
        variant: 'success',
        open: true,
        message: 'Deleted',
      });
      runHandleClose();
    },

    onError: (_, __, context: any) => {
      if (context?.previousBatches) {
        client.setQueryData(['extractionBatches', searchParams], context.previousBatches);
      }

      client.invalidateQueries({
        queryKey: ['extractionBatches']
      });
      setToast({
        variant: 'error',
        open: true,
        message: 'Sorry, something went wrong.',
      });
      runHandleClose();
    },

    onMutate: async (data) => {
      // IF type ==='menu', this is being called from the extraction batches page/table
      if (type === 'menu') {
        // Let's optimisitcally update the cache to remove the extraction batch

        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await client.cancelQueries({
          queryKey: ['extractionBatches', searchParams]
        });

        // // Snapshot the previous value
        const previousBatches: any = client.getQueryData(['extractionBatches', searchParams]);

        const previousResults = previousBatches?.results || [];

        const updatedResults = previousResults?.filter(
          (result: IExtractionBatch) => result.id !== extractionBatchId
        );

        const updatedCache = {
          ...previousBatches,
          count: Math.max((previousBatches?.count || 0) - 1, 0),
          results: updatedResults,
        };

        client.setQueryData(['extractionBatches', searchParams], updatedCache);

        // Return a context object with the snapshotted value
        return { previousBatches };
      }
    }
  });

  if (deleteExtractionBatch.isPending) {
    return <CircularProgress size={12} style={{ marginLeft: '13px', marginRight: '14px' }} />;
  }

  return (
    <>
      {type === 'menu' ? (
        <MenuItem
          key={'Delete'}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
          id="extraction-batch-delete"
          data-cy="btn-extraction-batch-delete"
          onClick={handleClickOpen}
          disabled={deleteExtractionBatch.isPending}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <MenuItemHiddenTextLabel>Delete</MenuItemHiddenTextLabel>
        </MenuItem>
      ) : type === 'icon' ? (
        <IconButtonWhiteBg
          disabled={deleteExtractionBatch.isPending}
          onClick={handleClickOpen}
          id="icon-extraction-batch-delete"
          cypressId="icon-extraction-batch-delete"
          tooltip="Delete Batch"
        >
          <DeleteIcon style={{ fontSize: '14px', color: 'black' }} />
        </IconButtonWhiteBg>
      ) : null}
      <Dialog
        open={open}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmation'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this extraction batch?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation}>Cancel</Button>
          <Button
            onClick={() => deleteExtractionBatch.mutate()}
            autoFocus
            data-cy="btn-extraction-batch-delete-modal-yes"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExtractionBatchDelete;
